// Extracted SLA & resolve-faster block for the Contact page
"use client";

import React from "react";
import { motion } from "framer-motion";
import {
  Clock,
  ShieldCheck,
  AlertTriangle,
  ListChecks,
  CheckCircle,
} from "lucide-react";
import type { SlaInfo } from "@/components/public/templates/ContactTemplate";

interface ContactSlaBlockProps {
  slaInfo?: SlaInfo;
  resolveChecklist?: string[];
}

export function ContactSlaBlock({ slaInfo, resolveChecklist }: ContactSlaBlockProps) {
  if (!slaInfo && !resolveChecklist) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
      {/* Response Expectations */}
      {slaInfo && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-2.5 mb-5">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white"
              style={{ backgroundColor: "var(--mininav-active-color, #a855f7)" }}
            >
              <ShieldCheck className="w-4 h-4" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 font-satoshi">
              Response Expectations &amp; SLAs
            </h3>
          </div>
          <div className="space-y-3">
            {[
              { label: "Standard", icon: Clock, iconClass: "text-gray-400", value: slaInfo.standard, border: "border-gray-100", bg: "bg-white" },
              { label: "Priority", icon: Clock, iconClass: "text-purple-500", value: slaInfo.priority, border: "border-gray-100", bg: "bg-white" },
              { label: "Emergency", icon: AlertTriangle, iconClass: "text-orange-500", value: slaInfo.emergency, border: "border-orange-100", bg: "bg-orange-50/50" },
            ].map((tier) => (
              <div key={tier.label} className={`rounded-xl border ${tier.border} ${tier.bg} p-4`}>
                <div className="flex items-center gap-2 mb-1">
                  <tier.icon className={`w-4 h-4 ${tier.iconClass}`} />
                  <span className="text-sm font-semibold text-gray-900">{tier.label}</span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{tier.value}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Resolve faster checklist */}
      {resolveChecklist && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
        >
          <div className="flex items-center gap-2.5 mb-5">
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center text-white"
              style={{ backgroundColor: "var(--mininav-active-color, #a855f7)" }}
            >
              <ListChecks className="w-4 h-4" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 font-satoshi">
              What to include so we can resolve faster
            </h3>
          </div>
          <div className="rounded-xl border border-gray-100 bg-white p-5">
            <ul className="space-y-3">
              {resolveChecklist.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-gray-600">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
                  <span className="leading-relaxed">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Emergency callout */}
          <div className="mt-4 rounded-xl border border-orange-100 bg-orange-50/50 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0 text-orange-500" />
              <div>
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  Emergency &amp; after-hours support
                </p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  If a property is at risk of missing check-in, select
                  &quot;Emergency / Guest-impacting incident&quot; in the
                  form. Include: unit ID, check-in time, access details,
                  and what&apos;s already been tried.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default ContactSlaBlock;
